import {
  WSAvatar,
  WSButton,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSIcon,
  WSLoader,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import { MemberWithholdingStatus } from "@wingspanhq/users/dist/lib/interfaces";
import React, { ReactNode } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { WSQueries } from "../../../../query/WSQuery";
import { useQueryInternalAccounts } from "../../../../query/banking/queries/useQueryInternalAccounts";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryPayoutSettings } from "../../../../query/payments/queries";
import { useQueryMemberProfile } from "../../../../query/users/queries";
import {
  avatarBankAccount,
  avatarWallet
} from "../../../../shared/constants/avatars";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { selectorDefaultDestination } from "../../../../shared/selectors/selectorDefaultDestination";
import { useTaxWithholdingsStatus } from "../../../Taxes/hooks/useTaxWithholdingsStatus";
import { PATH_TAXES } from "../../../Taxes/paths";
import { PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYOUT_METHOD } from "../../paths";
import { payoutDestinationTypeToMethodType } from "../payout/DefaultPayoutMethod";
import { SplitMethod } from "./SplitMethod";

type Props = {
  onEditWallet?: () => void;
} & WSElementProps;

export const PayoutSplits: React.FC<Props> = ({
  onEditWallet,
  ...elementProps
}) => {
  const userId = useUserId();
  const queryPayoutSettings = useQueryPayoutSettings(userId);
  const queryMember = useQueryMemberProfile(userId);
  const history = useHistory();
  const location = useLocation();

  const queryInternalAccounts = useQueryInternalAccounts();
  const bankingAccount = queryInternalAccounts.data?.find(
    selectorActiveBankingAccount
  );

  const { isActive, turnOn, turnOff, updateMeta, taxWithholdingAccount } =
    useTaxWithholdingsStatus();

  return (
    <WSElement {...elementProps}>
      <WSText.Paragraph weight="medium" mb="XL">
        Payout splits
      </WSText.Paragraph>
      <WSElement>
        <WSQueries
          queries={{
            queryPayoutSettings,
            queryMember
          }}
        >
          {({
            queryPayoutSettings: { data: payoutSettings },
            queryMember: { data: member }
          }) => {
            const destination = selectorDefaultDestination(payoutSettings);

            const walletItem = bankingAccount ? (
              <Item
                item={
                  <SplitMethod
                    type="InternalAccount"
                    id={bankingAccount.internalAccountId}
                    hideNumbers
                  />
                }
                onEdit={() => {
                  onEditWallet
                    ? onEditWallet()
                    : history.push("/member/wallet");
                }}
                value={`${destination ? 100 - destination.percentage : 100}%`}
              />
            ) : (
              <Item
                item={
                  <>
                    <WSAvatar size="S" {...avatarWallet} />
                    <WSText.ParagraphSm color="gray500">
                      Wingspan Wallet
                    </WSText.ParagraphSm>
                  </>
                }
                disabled
                onAdd={() => {
                  history.push("/member/wallet");
                }}
              />
            );

            const payoutMethodItem =
              destination && destination.destinationId ? (
                <Item
                  item={
                    <SplitMethod
                      type={
                        payoutDestinationTypeToMethodType(
                          destination.destinationType
                        )!
                      }
                      id={destination.destinationId}
                    />
                  }
                  value={`${destination.percentage}%`}
                />
              ) : bankingAccount ? (
                <Item
                  item={
                    <>
                      <WSAvatar size="S" {...avatarBankAccount} />
                      <WSText.ParagraphSm color="gray500">
                        No split method
                      </WSText.ParagraphSm>
                    </>
                  }
                  disabled
                  value="0%"
                />
              ) : (
                <Item
                  item={
                    <>
                      <WSAvatar size="S" {...avatarBankAccount} />
                      <WSText.ParagraphSm color="gray500">
                        Add payout method
                      </WSText.ParagraphSm>
                    </>
                  }
                  disabled
                  onAdd={() => {
                    history.push(
                      PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYOUT_METHOD
                    );
                  }}
                />
              );

            const texWithholdingItem = taxWithholdingAccount ? (
              <Item
                item={
                  <SplitMethod
                    type="InternalAccount"
                    id={taxWithholdingAccount.internalAccountId}
                    hideNumbers
                  />
                }
                onEdit={() => {
                  if (location.pathname.includes(PATH_TAXES)) {
                    isActive ? turnOff() : turnOn();
                  } else {
                    history.push(PATH_TAXES);
                  }
                }}
                value={
                  updateMeta.isLoading ? (
                    <WSLoader.Spinner size="XS" />
                  ) : member.profile.withholdings?.tax?.status ===
                    MemberWithholdingStatus.Active ? (
                    "On"
                  ) : (
                    "Off"
                  )
                }
              />
            ) : (
              <Item
                item={
                  <>
                    <WSAvatar size="S" {...avatarWallet} />
                    <WSText.ParagraphSm color="gray500">
                      Tax Withholding
                    </WSText.ParagraphSm>
                  </>
                }
                disabled
                onAdd={() => {
                  history.push(PATH_TAXES);
                }}
              />
            );

            const items: ReactNode[] = [];
            if (!destination && !!bankingAccount) {
              items.push(walletItem);
              items.push(payoutMethodItem);
            } else {
              items.push(payoutMethodItem);
              items.push(walletItem);
            }
            items.push(texWithholdingItem);

            return items.map((item, index) => (
              <WSElement
                key={String(index)}
                mb={index === items.length - 1 ? undefined : "M"}
              >
                {item}
              </WSElement>
            ));
          }}
        </WSQueries>
      </WSElement>
    </WSElement>
  );
};

type ItemProps = {
  item: ReactNode;
  disabled?: boolean;
  pending?: boolean;
  value?: ReactNode;
  onAdd?: () => void;
  onEdit?: () => void;
} & WSElementProps;

const Item: React.FC<ItemProps> = ({
  item,
  value,
  disabled,
  pending,
  onAdd,
  onEdit
}) => (
  <WSFlexBox justify="space-between" wrap="nowrap" alignItems="center">
    <WSFlexBox
      wrap="nowrap"
      alignItems="center"
      style={{ opacity: disabled ? 0.6 : undefined }}
      gap="S"
    >
      {item}
    </WSFlexBox>
    <WSFlexBox wrap="nowrap" alignItems="center">
      {onAdd && (
        <WSButton.Link size="M" icon="plus" onClick={onAdd}>
          Add
        </WSButton.Link>
      )}
      {onEdit && (
        <WSIcon block mr="M" name="edit" color="gray500" onClick={onEdit} />
      )}
      {value && <WSText.ParagraphSm>{value}</WSText.ParagraphSm>}
      {pending && <WSPill icon="time" text="Pending" />}
    </WSFlexBox>
  </WSFlexBox>
);
